/*
 * gravity-hero.js — CRU
 * Rigid-body playground behind the hero headline.
 *
 * Why it feels smooth
 *  - Physics runs on a FIXED 120 Hz step, decoupled from the display. Rendering
 *    interpolates between the two last physics states with the leftover
 *    accumulator, so there is no beat frequency between simulation and refresh
 *    rate (this is what removes the micro-jitter you get from a naive loop).
 *  - High-quality solver settings (10 position / 8 velocity iterations, tight
 *    slop) so stacks settle instead of buzzing.
 *  - Sprites are DOM <img> moved with translate3d only: no layout, no repaint,
 *    crisp at any DPR, and the browser still picks the right CDN size.
 *
 * Physics features
 *  - Collision shapes are traced from each PNG's alpha channel and convex-
 *    decomposed, so the bear cutter has a real hole and the rolling pin rolls.
 *  - The cursor is an actual rigid body: you can shove the pile without
 *    clicking. Grabbing uses a soft constraint, and releasing transfers the
 *    real pointer velocity, so throws have weight.
 *  - Auto-layout: sprite sizes are scaled to the hero so the objects always fit
 *    the width and never stack up into the type.
 *  - Scroll and device tilt bias the gravity vector.
 *
 * Add ?debug=1 to draw hulls, contacts and a perf meter.
 */
(function () {
  'use strict';

  var M = window.Matter;
  if (!M) return;

  var Engine = M.Engine,
    Composite = M.Composite,
    Bodies = M.Bodies,
    Body = M.Body,
    Constraint = M.Constraint,
    Vector = M.Vector,
    Query = M.Query,
    Events = M.Events,
    Sleeping = M.Sleeping;

  if (window.decomp && M.Common.setDecomp) M.Common.setDecomp(window.decomp);

  var DEBUG = /[?&]debug=1/.test(location.search);

  // Collision layers: walls and the cursor must not see each other.
  var CAT_ITEM = 0x0001, CAT_WALL = 0x0002, CAT_CURSOR = 0x0004;

  var DEFAULTS = {
    /* world */
    gravity: 1,
    fixedStep: 1000 / 120,   // physics tick
    maxSubsteps: 5,          // catch-up cap after a stall
    positionIterations: 10,
    velocityIterations: 8,
    constraintIterations: 3,

    /* default material */
    restitution: 0.24,
    friction: 0.42,
    frictionAir: 0.013,
    frictionStatic: 0.75,
    density: 0.0012,
    // Muy fino (0.01) hace que el solver pelee por solapes mínimos y los
    // cuerpos cóncavos entren en jitter permanente. 0.035 asienta limpio.
    slop: 0.035,

    /* shapes */
    traceRes: 118,
    maxVerts: 22,
    maxParts: 26,

    /* layout */
    fitScreen: true,         // fill the rest of the screen, minus the header…
    fitBelow: true,          // …and minus the strip right below the hero
    fillRatio: 1.55,         // total sprite width vs hero width
    // En móvil los anchos relativos son ridículos y todos acaban topando con su
    // mínimo en píxeles, que no escala con la pantalla: de ahí que se vean
    // diminutos. Se agrandan y se permite que la pila ocupe más de un ancho.
    mobileScale: 2.28,
    fillRatioMobile: 2.1,
    maxItemHeight: 0.38,     // cap each sprite against the hero height
    floorInset: 0.022,       // floor above the hero's bottom edge, so nothing
    floorInsetMin: 12,       //   gets clipped by whatever sits under the hero
    maxRestTop: 0.66,        // nothing may come to rest above this fraction…
    titleClearance: 44,      // …nor within this many px of the actual headline
                             //   (un objeto girado ocupa más caja que su alto)
    nudgeAttempts: 5,        //   box, whichever line is lower
    ceilingHeadroom: 0.6,
    mobileBreakpoint: 620,

    /* interaction */
    cursor: true,            // cursor as a physical body (mouse only)
    cursorRadius: 20,
    cursorStiffness: 0.85,
    cursorIdleMs: 220,       // a parked cursor stops being solid, so the pile
                             //   can fall asleep instead of resting on it
    grabStiffness: 0.16,
    pointerSmoothing: 0.42,
    throwScale: 1.05,
    maxSpeed: 26,
    maxSpin: 0.55,

    /* quiescence: a crowded pile can grind forever and never sleep, which
       would pin the hero at 60 fps for as long as the page is open */
    calmAfterMs: 2200,       // grace period after the last interaction
    calmSpeed: 2.5,          // only damp a scene that is already slow
    calmDamping: 0.985,      // per fixed step
    driftWindowMs: 420,      // jitter = velocity without displacement, so a
    driftEpsilon: 2.2,       //   body that hasn't actually moved gets slept

    /* motion coupling */
    scrollTilt: 0.5,         // gravity.x bias from scroll velocity
    tilt: false,             // device orientation on mobile (needs permission)

    /* looks */
    stagger: 170,
    dropOrder: 'ltr',        // ltr | rtl | center | edges | weight | random
    dropHeight: 0.22,        // spawn height above the hero, as a fraction of it
    squash: 0.085,
    shadow: true
  };

  /* ---------------------------------------------------------------- utils */

  function mulberry32(a) {
    return function () {
      a |= 0; a = (a + 0x6d2b79f5) | 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  function lerp(a, b, t) { return a + (b - a) * t; }

  function loadImage(src) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.decoding = 'async';
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('image failed: ' + src)); };
      img.src = src;
    });
  }

  /* ------------------------------------------------- silhouette → polygon */

  // Moore-neighbour contour tracing over the thresholded alpha channel.
  function traceContour(img, res) {
    var w = Math.max(8, Math.round(res));
    var h = Math.max(8, Math.round(res * img.naturalHeight / img.naturalWidth));

    var canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    var ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, w, h);

    var data;
    try {
      data = ctx.getImageData(0, 0, w, h).data;
    } catch (e) {
      return null; // tainted canvas: cross-origin image without CORS headers
    }

    function solid(x, y) {
      if (x < 0 || y < 0 || x >= w || y >= h) return false;
      return data[(y * w + x) * 4 + 3] > 96;
    }

    var sx = -1, sy = -1;
    for (var y = 0; y < h && sx < 0; y++) {
      for (var x = 0; x < w; x++) { if (solid(x, y)) { sx = x; sy = y; break; } }
    }
    if (sx < 0) return null;

    // 8-neighbourhood, clockwise in screen space (y down).
    var N = [[1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1], [1, -1]];
    var contour = [];
    var px = sx, py = sy, back = 4; // we arrived from the west
    var guard = w * h * 4;

    do {
      contour.push([px, py]);
      var moved = false;
      for (var k = 1; k <= 8; k++) {
        var idx = (back + k) % 8;
        var nx = px + N[idx][0], ny = py + N[idx][1];
        if (!solid(nx, ny)) continue;
        // The neighbour checked just before becomes the new backtrack cell.
        var prev = (back + k - 1 + 8) % 8;
        var bx = px + N[prev][0], by = py + N[prev][1];
        px = nx; py = ny; back = 4;
        for (var n = 0; n < 8; n++) {
          if (N[n][0] === bx - px && N[n][1] === by - py) { back = n; break; }
        }
        moved = true;
        break;
      }
      if (!moved) break;
    } while (!(px === sx && py === sy) && contour.length < guard);

    return contour.length >= 8 ? contour : null;
  }

  function perpDistance(p, a, b) {
    var dx = b[0] - a[0], dy = b[1] - a[1];
    var len = Math.hypot(dx, dy);
    if (len < 1e-9) return Math.hypot(p[0] - a[0], p[1] - a[1]);
    return Math.abs(dy * (p[0] - a[0]) - dx * (p[1] - a[1])) / len;
  }

  // Ramer–Douglas–Peucker, iterative (contours can be thousands of points).
  function simplify(points, eps) {
    if (points.length < 3) return points.slice();
    var keep = new Uint8Array(points.length);
    keep[0] = keep[points.length - 1] = 1;
    var stack = [[0, points.length - 1]];
    while (stack.length) {
      var seg = stack.pop(), a = seg[0], b = seg[1];
      var best = -1, bestI = -1;
      for (var i = a + 1; i < b; i++) {
        var d = perpDistance(points[i], points[a], points[b]);
        if (d > best) { best = d; bestI = i; }
      }
      if (best > eps && bestI > 0) {
        keep[bestI] = 1;
        stack.push([a, bestI], [bestI, b]);
      }
    }
    var out = [];
    for (var j = 0; j < points.length; j++) if (keep[j]) out.push(points[j]);
    return out;
  }

  // Traced once per image at unit scale, then reused for every resize.
  function traceUnitHull(img, cfg) {
    var contour = traceContour(img, cfg.traceRes);
    if (!contour) return null;

    var eps = 1.05, simplified = contour;
    for (var pass = 0; pass < 14; pass++) {
      simplified = simplify(contour, eps);
      if (simplified.length <= cfg.maxVerts) break;
      eps *= 1.32;
    }
    if (simplified.length < 4) return null;

    var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    simplified.forEach(function (p) {
      if (p[0] < minX) minX = p[0];
      if (p[0] > maxX) maxX = p[0];
      if (p[1] < minY) minY = p[1];
      if (p[1] > maxY) maxY = p[1];
    });
    var sx = (maxX - minX) > 1e-6 ? 1 / (maxX - minX) : 1;
    var sy = (maxY - minY) > 1e-6 ? 1 / (maxY - minY) : 1;

    // Normalised to the 0..1 sprite box, so hull and pixels always line up.
    return simplified.map(function (p) {
      return { x: (p[0] - minX) * sx, y: (p[1] - minY) * sy };
    });
  }

  /* --------------------------------------------------------------- config */

  // Merchant-facing settings are plain integers (Shopify range sliders);
  // translate them into physics units here so the Liquid stays dumb.
  function normalizeSpec(s) {
    if (s.widthPct != null && s.w == null) s.w = s.widthPct / 100;
    if (s.weight != null && s.density == null) s.density = 0.0003 * s.weight;
    if (s.grip != null && s.friction == null) s.friction = s.grip / 100;
    if (s.bounce != null && s.restitution == null) s.restitution = s.bounce / 100;
    return s;
  }

  function readConfig(root) {
    var node = root.querySelector('[data-gravity-config]');
    var raw = {};
    if (node) {
      try { raw = JSON.parse(node.textContent); } catch (e) { raw = {}; }
    }
    var cfg = Object.assign({}, DEFAULTS, raw.options || {});
    cfg.items = (raw.items || []).slice();

    // One <script type="application/json" data-gravity-item> per object: this
    // is how each Shopify child block contributes itself to the scene.
    root.querySelectorAll('[data-gravity-item]').forEach(function (n) {
      try {
        var item = JSON.parse(n.textContent);
        if (item && item.src) cfg.items.push(item);
      } catch (e) { /* ignore a malformed block */ }
    });

    cfg.items = cfg.items.map(normalizeSpec);
    return cfg;
  }

  /* ----------------------------------------------------------------- main */

  function createHero(root) {
    var cfg = readConfig(root);
    if (!cfg.items.length) return;

    var layer = root.querySelector('[data-gravity-layer]');
    if (!layer) return;

    // A separate upper layer lets the held object cross the headline without
    // lifting the whole pile above the type.
    var dragLayer = document.createElement('div');
    dragLayer.className = 'gh-drag-layer';
    dragLayer.setAttribute('aria-hidden', 'true');
    root.appendChild(dragLayer);

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var engine = Engine.create({
      enableSleeping: true,
      gravity: { x: 0, y: cfg.gravity, scale: 0.001 },
      positionIterations: cfg.positionIterations,
      velocityIterations: cfg.velocityIterations,
      constraintIterations: cfg.constraintIterations
    });

    var items = [];
    var bodies = [];      // top-level item bodies, in z order
    var byBodyId = {};
    var walls = [];
    var size = { w: 0, h: 0 };
    var rng = mulberry32(0x43525521);

    var drag = null;
    var cursorBody = null;
    var cursorLink = null;
    var pointer = {
      x: 0, y: 0,          // raw
      sx: 0, sy: 0,        // smoothed (used by physics)
      cx: null, cy: null,   // viewport coords: distinguish a real mouse move
      inside: false,
      type: 'mouse',
      movedAt: 0,
      history: []          // {x, y, t} for throw velocity
    };

    var running = false, started = false, pending = 0, rafId = 0, generation = 0;
    var lastTime = 0, accumulator = 0;
    var scrollTilt = 0, lastScrollY = window.scrollY, scrollVel = 0;
    var lastInteraction = 0, sceneSpeed = 0;
    var tiltX = 0;
    var frames = 0, fpsTime = 0, fps = 0, stepsLastFrame = 0, settleCheck = 0;
    var debugCanvas = null, debugCtx = null;

    /* ------------------------------------------------------------ sizing */

    // Whatever sits above the hero (announcement bar, header) is discounted so
    // the section fills exactly the rest of the screen. Measured, not hardcoded:
    // on Shopify the header height depends on the merchant's settings.
    function fitToScreen() {
      if (!cfg.fitScreen) return;
      root.style.setProperty('--gh-top-offset', '0px');
      root.style.setProperty('--gh-bottom-reserve', '0px');

      var top = root.getBoundingClientRect().top + window.scrollY;
      // Only discount a real header; if the hero lives further down the page,
      // subtracting its offset would collapse it.
      var offset = top > 0 && top < window.innerHeight * 0.4 ? Math.round(top) : 0;

      // Keep whatever comes right after (the NEW ARRIVALS marquee) on screen.
      var reserve = 0;
      if (cfg.fitBelow) {
        // On the real home page the next sections are disabled and render as
        // empty divs, so walk forward until something actually has height.
        var next = (root.closest('.shopify-section') || root).nextElementSibling;
        for (var hops = 0; next && hops < 6; hops++) {
          var nh = next.getBoundingClientRect().height;
          if (nh > 0.5) {
            if (nh < window.innerHeight * 0.3) reserve = Math.round(nh);
            break;
          }
          next = next.nextElementSibling;
        }
      }

      root.style.setProperty('--gh-top-offset', offset + 'px');
      root.style.setProperty('--gh-bottom-reserve', reserve + 'px');
    }

    // The line nothing may rest above. Derived from the real headline box, so
    // it follows the font size, the language and the screen instead of trusting
    // a magic fraction.
    function restCeiling() {
      var limit = size.h * cfg.maxRestTop;
      var title = root.querySelector('.gh-title');
      if (title) {
        var rect = root.getBoundingClientRect();
        var bottom = title.getBoundingClientRect().bottom - rect.top + cfg.titleClearance;
        if (bottom > limit) limit = bottom;
      }
      return Math.min(limit, size.h * 0.9);
    }

    function measure() {
      fitToScreen();
      var rect = root.getBoundingClientRect();
      size.w = Math.max(1, rect.width);
      size.h = Math.max(1, rect.height);
    }

    function activeSpecs() {
      var mobile = size.w < cfg.mobileBreakpoint;
      return cfg.items.filter(function (it) { return !mobile || it.mobile !== false; });
    }

    // Two passes: per-item clamps, then one global factor so the row always
    // fits the hero. Guarantees objects never pile up into the headline.
    function layoutSizes(specs) {
      var mobile = size.w < cfg.mobileBreakpoint;
      var scale = mobile ? cfg.mobileScale : 1;
      var fill = mobile ? cfg.fillRatioMobile : cfg.fillRatio;

      // Ningún objeto puede ser más alto que el hueco libre entre el suelo y el
      // titular: si lo fuera, al reposar taparía el texto y no habría empujón
      // que lo arregle, porque ya está en el suelo. Es el tope que manda.
      var banda = floorY() - restCeiling();
      var maxH = Math.min(size.h * cfg.maxItemHeight, Math.max(60, banda));

      var sizes = specs.map(function (spec) {
        var img = spec._img;
        var ratio = img.naturalHeight / img.naturalWidth;
        // El mínimo en píxeles es un suelo, no el tamaño de referencia: en
        // móvil se escala primero para que el objeto crezca con la pantalla.
        var w = size.w * (spec.w || 0.15) * scale;
        w = clamp(w, spec.min || 56, (spec.max || 420) * scale);
        if (w * ratio > maxH) w = maxH / ratio;
        return { w: w, h: w * ratio, ratio: ratio };
      });

      var total = sizes.reduce(function (sum, s) { return sum + s.w; }, 0);
      var factor = total > 0 ? Math.min(1, fill * size.w / total) : 1;
      if (factor < 1) {
        sizes.forEach(function (s) { s.w *= factor; s.h *= factor; });
        total *= factor;
      }

      // Spawn columns follow the config order, spread across the hero width.
      var pad = Math.max(0, size.w - total) / 2;
      var cursorX = pad;
      sizes.forEach(function (s) {
        s.x = cursorX + s.w / 2;
        cursorX += s.w;
      });
      sizes.forEach(function (s) {
        s.w = Math.round(s.w); s.h = Math.round(s.h);
        s.x = clamp(s.x, s.w * 0.5 + 2, size.w - s.w * 0.5 - 2);
      });
      return sizes;
    }

    /* ------------------------------------------------------------- world */

    // The objects must come to rest fully inside the hero, not clipped by its
    // bottom edge (there is a marquee strip right underneath).
    function floorY() {
      return size.h - Math.max(cfg.floorInsetMin, size.h * cfg.floorInset);
    }

    function buildWalls() {
      walls.forEach(function (b) { Composite.remove(engine.world, b); });
      var t = 400; // thick: nothing tunnels through
      var head = size.h * cfg.ceilingHeadroom;
      var ground = floorY();
      var opts = {
        isStatic: true, friction: 0.65, restitution: 0.06, slop: cfg.slop,
        collisionFilter: { category: CAT_WALL, mask: CAT_ITEM }, label: 'wall'
      };
      walls = [
        Bodies.rectangle(size.w / 2, ground + t / 2, size.w + t * 2, t, opts),
        Bodies.rectangle(-t / 2, size.h / 2 - head, t, size.h * 3 + head * 2, opts),
        Bodies.rectangle(size.w + t / 2, size.h / 2 - head, t, size.h * 3 + head * 2, opts),
        Bodies.rectangle(size.w / 2, -head - t / 2, size.w + t * 2, t, opts)
      ];
      Composite.add(engine.world, walls);
    }

    function makeBody(spec, w, h) {
      var opts = {
        restitution: spec.restitution != null ? spec.restitution : cfg.restitution,
        friction: spec.friction != null ? spec.friction : cfg.friction,
        frictionAir: spec.frictionAir != null ? spec.frictionAir : cfg.frictionAir,
        frictionStatic: cfg.frictionStatic,
        density: spec.density != null ? spec.density : cfg.density,
        slop: cfg.slop,
        sleepThreshold: 50,
        collisionFilter: { category: CAT_ITEM, mask: CAT_ITEM | CAT_WALL | CAT_CURSOR },
        label: spec.src
      };

      var body = null;
      if (spec._hull) {
        var verts = spec._hull.map(function (p) { return { x: p.x * w, y: p.y * h }; });
        try {
          body = Bodies.fromVertices(0, 0, [verts], opts, true, 0.01, 12, 0.01);
        } catch (e) {
          body = null;
        }
        if (body && (!isFinite(body.area) || body.area <= 0 || body.parts.length > cfg.maxParts)) {
          body = null;
        }
      }
      if (!body) {
        body = Bodies.rectangle(0, 0, w, h, Object.assign({
          chamfer: { radius: Math.min(w, h) * 0.24 }
        }, opts));
      }

      // Offset from the body origin to the sprite's top-left corner: Matter
      // re-centres vertices on the centre of mass, the sprite must follow.
      var parts = body.parts.length > 1 ? body.parts.slice(1) : body.parts;
      var minX = Infinity, minY = Infinity;
      parts.forEach(function (part) {
        part.vertices.forEach(function (v) {
          if (v.x < minX) minX = v.x;
          if (v.y < minY) minY = v.y;
        });
      });
      Body.setPosition(body, { x: 0, y: 0 });

      return { body: body, ox: minX, oy: minY, traced: !!spec._hull };
    }

    function syncPrev(it) {
      it.px = it.body.position.x;
      it.py = it.body.position.y;
      it.pa = it.body.angle;
    }

    // In which order the objects drop in. Items are already laid out left to
    // right, so index order is the horizontal cascade.
    function dropSequence() {
      var list = items.slice();
      var mid = (items.length - 1) / 2;
      switch (cfg.dropOrder) {
        case 'rtl': return list.reverse();
        case 'weight': return list.sort(function (a, b) { return b.body.mass - a.body.mass; });
        case 'center': return list.sort(function (a, b) {
          return Math.abs(a.z - mid) - Math.abs(b.z - mid);
        });
        case 'edges': return list.sort(function (a, b) {
          return Math.abs(b.z - mid) - Math.abs(a.z - mid);
        });
        case 'random': return list.sort(function () { return rng() - 0.5; });
        default: return list; // 'ltr'
      }
    }

    function buildItems(preserve) {
      // Bump first and unconditionally: any pending entrance timeout from an
      // earlier build must be invalidated, or it will re-add a discarded body
      // to the world as an invisible collider.
      generation++;
      if (drag) endDrag();
      dragLayer.textContent = '';

      var previous = {};
      if (preserve) {
        items.forEach(function (it) {
          previous[it.spec.src] = {
            x: it.body.position.x / size.w,
            y: it.body.position.y / size.h,
            angle: it.body.angle
          };
        });
      }

      items.forEach(function (it) { Composite.remove(engine.world, it.body); });
      layer.textContent = '';
      items = []; bodies = []; byBodyId = {}; pending = 0;

      var specs = activeSpecs();
      if (!specs.length) return;
      var sizes = layoutSizes(specs);

      specs.forEach(function (spec, i) {
        var s = sizes[i];
        var made = makeBody(spec, s.w, s.h);
        var body = made.body;

        var prev = previous[spec.src];
        var x, y, angle;
        if (prev) {
          x = clamp(prev.x * size.w, s.w * 0.5, size.w - s.w * 0.5);
          y = clamp(prev.y * size.h, -size.h, size.h - s.h * 0.3);
          angle = prev.angle;
        } else {
          x = s.x;
          // Similar fall distances so the cascade reads as timing, not chaos.
          y = reduced ? floorY() - s.h * 0.5 - 1
            : -s.h * 0.5 - size.h * cfg.dropHeight - rng() * size.h * 0.1;
          angle = spec.angle != null ? spec.angle : (rng() - 0.5) * 0.55;
        }
        Body.setPosition(body, { x: x, y: y });
        Body.setAngle(body, angle);
        Body.setVelocity(body, { x: 0, y: 0 });
        Body.setAngularVelocity(body, reduced || prev ? 0 : (rng() - 0.5) * 0.09);

        var el = document.createElement('div');
        el.className = 'gh-item';
        el.style.width = s.w + 'px';
        el.style.height = s.h + 'px';
        el.style.zIndex = String(10 + i);
        var sprite = document.createElement('img');
        sprite.className = 'gh-sprite';
        sprite.src = spec.src;
        sprite.alt = '';
        sprite.setAttribute('aria-hidden', 'true');
        sprite.draggable = false;
        el.appendChild(sprite);
        layer.appendChild(el);

        var item = {
          spec: spec, el: el, sprite: sprite, body: body,
          w: s.w, h: s.h, ox: made.ox, oy: made.oy, traced: made.traced,
          squash: 0, z: i, visible: false, shown: false, nudges: 0,
          px: x, py: y, pa: angle, lastKey: ''
        };
        items.push(item);
        bodies.push(body);
        byBodyId[body.id] = item;
      });

      var order = dropSequence();

      if (reduced || preserve) {
        items.forEach(function (it) { it.visible = true; syncPrev(it); });
        Composite.add(engine.world, bodies);
        if (reduced) {
          for (var i = 0; i < 320; i++) Engine.update(engine, cfg.fixedStep);
          items.forEach(function (it) {
            Body.setStatic(it.body, true);
            syncPrev(it);
          });
        }
        items.forEach(function (it) { render(it, 1); });
      } else {
        var gen = generation;
        pending = order.length;
        lastInteraction = performance.now();
        order.forEach(function (it, i) {
          setTimeout(function () {
            if (gen !== generation) return; // rebuilt while we waited
            pending--;
            it.visible = true;
            syncPrev(it);
            Composite.add(engine.world, it.body);
            render(it, 1);
            start();
          }, i * cfg.stagger);
        });
      }
    }

    /* ------------------------------------------------------ cursor body */

    function addCursorBody() {
      if (!cfg.cursor || cursorBody || pointer.type !== 'mouse' || reduced) return;
      var r = cfg.cursorRadius;
      cursorBody = Bodies.circle(pointer.x, pointer.y, r, {
        density: 0.006,
        frictionAir: 0.32,
        friction: 0.2,
        restitution: 0.1,
        slop: cfg.slop,
        inertia: Infinity,          // never spins: it is a hand, not a ball
        collisionFilter: { category: CAT_CURSOR, mask: CAT_ITEM },
        label: 'cursor'
      });
      cursorLink = Constraint.create({
        pointA: { x: pointer.x, y: pointer.y },
        bodyB: cursorBody,
        length: 0,
        stiffness: cfg.cursorStiffness,
        damping: 0.2,
        render: { visible: false }
      });
      Composite.add(engine.world, [cursorBody, cursorLink]);
    }

    function removeCursorBody() {
      if (!cursorBody) return;
      Composite.remove(engine.world, cursorLink);
      Composite.remove(engine.world, cursorBody);
      cursorBody = null;
      cursorLink = null;
    }

    /* ----------------------------------------------------------- pointer */

    function toLocal(event) {
      var rect = root.getBoundingClientRect();
      return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    }

    function pushHistory(x, y) {
      var now = performance.now();
      pointer.history.push({ x: x, y: y, t: now });
      while (pointer.history.length > 8 || (pointer.history.length > 2 && now - pointer.history[0].t > 110)) {
        pointer.history.shift();
      }
    }

    // px per 16.67 ms, matching Matter's velocity units.
    function pointerVelocity() {
      var h = pointer.history;
      if (h.length < 2) return { x: 0, y: 0 };
      var a = h[0], b = h[h.length - 1];
      var dt = b.t - a.t;
      if (dt < 8) return { x: 0, y: 0 };
      return { x: (b.x - a.x) / dt * 16.667, y: (b.y - a.y) / dt * 16.667 };
    }

    function bodyAt(point) {
      var hit = Query.point(bodies, point);
      if (!hit.length) return null;
      var top = hit[0];
      for (var i = 1; i < hit.length; i++) {
        if (bodies.indexOf(hit[i]) > bodies.indexOf(top)) top = hit[i];
      }
      return top;
    }

    function onPointerDown(event) {
      if (reduced || event.button > 0) return;
      pointer.type = event.pointerType || 'mouse';
      var p = toLocal(event);
      pointer.x = pointer.sx = p.x;
      pointer.y = pointer.sy = p.y;
      pointer.cx = event.clientX;
      pointer.cy = event.clientY;
      pointer.movedAt = performance.now();
      pointer.history.length = 0;
      pushHistory(p.x, p.y);

      var body = bodyAt(p);
      if (!body) { start(); return; }

      event.preventDefault();
      if (root.setPointerCapture) root.setPointerCapture(event.pointerId);
      root.classList.add('is-dragging');
      lastInteraction = performance.now();
      root.style.touchAction = 'none'; // only while something is in hand

      removeCursorBody();
      Sleeping.set(body, false);

      var local = Vector.rotate(Vector.sub(p, body.position), -body.angle);
      drag = {
        pointerId: event.pointerId,
        body: body,
        constraint: Constraint.create({
          pointA: { x: p.x, y: p.y },
          bodyB: body,
          pointB: local,
          length: 0,
          stiffness: cfg.grabStiffness,
          damping: 0.04,
          angularStiffness: 0,   // free to swing around the grab point
          render: { visible: false }
        })
      };
      Composite.add(engine.world, drag.constraint);

      var item = byBodyId[body.id];
      if (item) {
        dragLayer.appendChild(item.el);
        item.el.style.zIndex = '1';
        item.el.classList.add('is-held');
      }
      start();
    }

    function onPointerMove(event) {
      pointer.type = event.pointerType || 'mouse';
      var p = toLocal(event);
      var realMove = pointer.cx == null ||
        event.clientX !== pointer.cx || event.clientY !== pointer.cy;
      pointer.cx = event.clientX;
      pointer.cy = event.clientY;
      pointer.inside = p.x >= 0 && p.y >= 0 && p.x <= size.w && p.y <= size.h;
      // Only a real move counts. Browsers also emit pointermove when the page
      // shifts under a parked cursor, which would keep the pile awake forever.
      if (realMove || drag) {
        pointer.x = p.x;
        pointer.y = p.y;
      }
      if (realMove) {
        pointer.movedAt = lastInteraction = performance.now();
        pushHistory(p.x, p.y);
      }

      if (drag && event.pointerId === drag.pointerId) {
        event.preventDefault();
        start();
        return;
      }
      if (reduced) return;
      if (pointer.inside) {
        if (realMove) {
          if (!cursorBody) { pointer.sx = p.x; pointer.sy = p.y; addCursorBody(); }
          root.classList.toggle('is-over', !!bodyAt(p));
          start();
        }
      } else if (cursorBody) {
        removeCursorBody();
        root.classList.remove('is-over');
      }
    }

    function endDrag(event) {
      if (!drag || (event && event.pointerId !== drag.pointerId)) return;
      var body = drag.body;
      Composite.remove(engine.world, drag.constraint);

      // Hand over the real pointer velocity so the throw has weight.
      var pv = pointerVelocity();
      var bv = Body.getVelocity ? Body.getVelocity(body) : body.velocity;
      var vx = lerp(bv.x, pv.x, 0.65) * cfg.throwScale;
      var vy = lerp(bv.y, pv.y, 0.65) * cfg.throwScale;
      var speed = Math.hypot(vx, vy);
      if (speed > cfg.maxSpeed) { vx = vx / speed * cfg.maxSpeed; vy = vy / speed * cfg.maxSpeed; }
      Body.setVelocity(body, { x: vx, y: vy });

      var item = byBodyId[body.id];
      if (item) {
        layer.appendChild(item.el);
        item.el.style.zIndex = String(10 + item.z);
        item.el.classList.remove('is-held');
      }
      drag = null;
      root.classList.remove('is-dragging');
      root.style.touchAction = '';
      if (pointer.inside && pointer.type === 'mouse') addCursorBody();
      start();
    }

    /* ------------------------------------------------------------ render */

    // alpha = how far we are between the last two physics states.
    function render(it, alpha) {
      var b = it.body;
      var x = lerp(it.px, b.position.x, alpha);
      var y = lerp(it.py, b.position.y, alpha);
      var a = lerp(it.pa, b.angle, alpha);

      var key = (x * 16 | 0) + ',' + (y * 16 | 0) + ',' + (a * 4096 | 0);
      if (key === it.lastKey) return false;
      it.lastKey = key;

      it.el.style.transform =
        'translate3d(' + x.toFixed(2) + 'px,' + y.toFixed(2) + 'px,0)' +
        ' rotate(' + a.toFixed(4) + 'rad)' +
        ' translate(' + it.ox.toFixed(2) + 'px,' + it.oy.toFixed(2) + 'px)';

      if (!it.shown) { it.el.classList.add('is-live'); it.shown = true; }

      if (cfg.squash > 0) {
        if (it.squash > 0.002) {
          it.sprite.style.transform = 'scale(' + (1 + it.squash * 0.5).toFixed(4) +
            ',' + (1 - it.squash).toFixed(4) + ')';
        } else if (it.sprite.style.transform) {
          it.sprite.style.transform = '';
        }
      }
      return true;
    }

    /* -------------------------------------------------------------- loop */

    function physicsStep(now) {
      // Once the scene is slow and nobody has touched it for a while, bleed off
      // the residual jostling so Matter's sleeping can actually kick in.
      var calm = (now - lastInteraction) > cfg.calmAfterMs && sceneSpeed < cfg.calmSpeed;

      // Gravity leans with scroll and device tilt: the pile reacts to the page.
      var gx = clamp(scrollVel * 0.004 * cfg.scrollTilt + tiltX, -0.75, 0.75);
      engine.gravity.x = gx;
      engine.gravity.y = cfg.gravity * Math.sqrt(Math.max(0.05, 1 - gx * gx * 0.35));

      if (cursorLink) cursorLink.pointA = { x: pointer.sx, y: pointer.sy };
      if (drag) {
        drag.constraint.pointA = { x: pointer.sx, y: pointer.sy };
        Sleeping.set(drag.body, false);
      }

      items.forEach(function (it) { if (it.visible) syncPrev(it); });
      Engine.update(engine, cfg.fixedStep);

      // Clamp after integration: keeps fast throws inside the box.
      var fastest = 0;
      items.forEach(function (it) {
        if (!it.visible) return;
        var b = it.body;
        if (b.isSleeping) return;
        var v = Body.getVelocity ? Body.getVelocity(b) : b.velocity;
        var s = Math.hypot(v.x, v.y);
        if (s > fastest) fastest = s;

        if (s > cfg.maxSpeed) {
          Body.setVelocity(b, { x: v.x / s * cfg.maxSpeed, y: v.y / s * cfg.maxSpeed });
        } else if (calm) {
          Body.setVelocity(b, { x: v.x * cfg.calmDamping, y: v.y * cfg.calmDamping });
        }
        if (Math.abs(b.angularVelocity) > cfg.maxSpin) {
          Body.setAngularVelocity(b, cfg.maxSpin * Math.sign(b.angularVelocity));
        } else if (calm) {
          Body.setAngularVelocity(b, b.angularVelocity * cfg.calmDamping);
        }
      });
      sceneSpeed = fastest;
    }

    function frame(now) {
      rafId = 0;
      if (!lastTime) lastTime = now;
      var dt = Math.min(now - lastTime, 100);
      lastTime = now;

      // Pointer smoothing: gives the grab weight and filters mouse noise.
      var k = 1 - Math.pow(1 - cfg.pointerSmoothing, dt / 16.667);
      pointer.sx += (pointer.x - pointer.sx) * k;
      pointer.sy += (pointer.y - pointer.sy) * k;
      scrollVel *= Math.pow(0.86, dt / 16.667);

      // A cursor left parked would hold objects up and keep the pile awake.
      if (cursorBody && !drag && now - pointer.movedAt > cfg.cursorIdleMs) {
        removeCursorBody();
      }

      accumulator = Math.min(accumulator + dt, cfg.fixedStep * cfg.maxSubsteps);
      stepsLastFrame = 0;
      while (accumulator >= cfg.fixedStep) {
        physicsStep(now);
        accumulator -= cfg.fixedStep;
        stepsLastFrame++;
      }

      var alpha = accumulator / cfg.fixedStep;
      var moving = !!drag || !!cursorBody;
      items.forEach(function (it) {
        if (!it.visible) return;
        if (it.squash > 0) it.squash *= Math.pow(0.86, dt / 16.667);
        if (!it.body.isSleeping || it.squash > 0.002 || !it.shown) {
          render(it, alpha);
          moving = true;
        }
      });

      // Two bodies can fight over a contact forever: real velocity, zero
      // displacement. Damping can't beat the solver, so measure actual drift
      // and put anything that isn't going anywhere to sleep.
      if ((now - lastInteraction) > cfg.calmAfterMs) {
        items.forEach(function (it) {
          var b = it.body;
          if (!it.visible || b.isSleeping) { it.driftRef = null; return; }
          if (!it.driftRef) {
            it.driftRef = { x: b.position.x, y: b.position.y, t: now };
            return;
          }
          if (now - it.driftRef.t < cfg.driftWindowMs) return;
          var moved = Math.hypot(b.position.x - it.driftRef.x, b.position.y - it.driftRef.y);
          if (moved < cfg.driftEpsilon) {
            Body.setVelocity(b, { x: 0, y: 0 });
            Body.setAngularVelocity(b, 0);
            Sleeping.set(b, true);
          }
          it.driftRef = { x: b.position.x, y: b.position.y, t: now };
        });
      }

      // Safety net: an object that settles too high (perched on a neighbour,
      // over the type) gets shoved sideways until it slides down.
      if (cfg.maxRestTop < 1 && ++settleCheck > 24) {
        settleCheck = 0;
        var ground = floorY();
        var ceiling = restCeiling();
        items.forEach(function (it) {
          if (!it.visible || it.nudges >= cfg.nudgeAttempts) return;
          if (it.body.bounds.min.y >= ceiling) return;
          // A tall object resting normally on the floor is not "too high".
          // Only dislodge bodies genuinely perched on top of the pile.
          if (ground - it.body.bounds.max.y < Math.max(8, it.h * 0.06)) return;
          // "Come to rest" means asleep OR barely moving: a body propped up
          // against the cursor never actually sleeps.
          var speed = Body.getSpeed ? Body.getSpeed(it.body) : 0;
          if (!it.body.isSleeping && speed > 0.3) return;
          it.nudges++;
          var dir = it.body.position.x < size.w / 2 ? -1 : 1;
          Sleeping.set(it.body, false);
          Body.applyForce(it.body, it.body.position,
            { x: dir * 0.0013 * (1 + it.nudges) * it.body.mass, y: 0 });
          Body.setAngularVelocity(it.body, dir * 0.05);
          moving = true;
        });
      }

      frames++;
      if (now - fpsTime > 500) {
        fps = Math.round(frames * 1000 / (now - fpsTime));
        frames = 0; fpsTime = now;
      }
      if (DEBUG) drawDebug();

      if (running && (moving || pending > 0 || Math.abs(scrollVel) > 0.5 || DEBUG)) {
        rafId = requestAnimationFrame(frame);
      } else {
        running = false; // everything asleep — stop burning frames
      }
    }

    function start() {
      if (reduced || running) return;
      running = true;
      lastTime = 0;
      accumulator = 0;
      if (!rafId) rafId = requestAnimationFrame(frame);
    }

    function stop() {
      running = false;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = 0;
    }

    /* ------------------------------------------------------------- debug */

    function drawDebug() {
      if (!debugCtx) return;
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      if (debugCanvas.width !== Math.round(size.w * dpr)) {
        debugCanvas.width = Math.round(size.w * dpr);
        debugCanvas.height = Math.round(size.h * dpr);
      }
      debugCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
      debugCtx.clearRect(0, 0, size.w, size.h);
      debugCtx.lineWidth = 1;

      var parts = 0, asleep = 0;
      items.forEach(function (it) {
        if (!it.visible) return;
        if (it.body.isSleeping) asleep++;
        var list = it.body.parts.length > 1 ? it.body.parts.slice(1) : it.body.parts;
        parts += list.length;
        debugCtx.strokeStyle = it.body.isSleeping ? 'rgba(255,255,255,0.55)'
          : it.traced ? 'rgba(255,0,90,0.95)' : 'rgba(0,180,255,0.95)';
        list.forEach(function (part) {
          debugCtx.beginPath();
          part.vertices.forEach(function (v, i) {
            if (i === 0) debugCtx.moveTo(v.x, v.y); else debugCtx.lineTo(v.x, v.y);
          });
          debugCtx.closePath();
          debugCtx.stroke();
        });
      });

      if (cursorBody) {
        debugCtx.strokeStyle = 'rgba(0,255,120,0.95)';
        debugCtx.beginPath();
        debugCtx.arc(cursorBody.position.x, cursorBody.position.y, cfg.cursorRadius, 0, 6.2832);
        debugCtx.stroke();
      }

      debugCtx.fillStyle = 'rgba(0,0,0,0.78)';
      debugCtx.fillRect(8, 8, 268, 58);
      debugCtx.fillStyle = '#fff';
      debugCtx.font = '12px ui-monospace, monospace';
      debugCtx.fillText('fps ' + fps + '  steps/frame ' + stepsLastFrame +
        '  g ' + engine.gravity.x.toFixed(2), 16, 26);
      debugCtx.fillText('bodies ' + items.length + '  parts ' + parts +
        '  asleep ' + asleep, 16, 42);
      debugCtx.fillText('traced red / box blue / sleeping white', 16, 58);
    }

    /* -------------------------------------------------------------- bind */

    function bind() {
      root.addEventListener('pointerdown', onPointerDown);
      root.addEventListener('pointermove', onPointerMove, { passive: false });
      root.addEventListener('pointerup', endDrag);
      root.addEventListener('pointercancel', endDrag);
      root.addEventListener('pointerleave', function () {
        pointer.inside = false;
        removeCursorBody();
        root.classList.remove('is-over');
      });
      window.addEventListener('blur', function () { endDrag(); removeCursorBody(); });

      Events.on(engine, 'collisionStart', function (e) {
        if (cfg.squash <= 0) return;
        e.pairs.forEach(function (pair) {
          var n = pair.collision.normal;
          var a = pair.bodyA.parent, b = pair.bodyB.parent;
          var va = Body.getVelocity ? Body.getVelocity(a) : a.velocity;
          var vb = Body.getVelocity ? Body.getVelocity(b) : b.velocity;
          var impact = Math.abs((vb.x - va.x) * n.x + (vb.y - va.y) * n.y);
          var amount = clamp(impact / 15, 0, 1) * cfg.squash;
          [a, b].forEach(function (body) {
            var it = byBodyId[body.id];
            if (it) it.squash = Math.max(it.squash, amount);
          });
        });
      });

      window.addEventListener('scroll', function () {
        var y = window.scrollY;
        scrollVel = clamp(scrollVel + (y - lastScrollY), -120, 120);
        lastInteraction = performance.now();
        lastScrollY = y;
        if (Math.abs(scrollVel) > 1) start();
      }, { passive: true });

      document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'visible') { lastTime = 0; start(); }
      });

      if (cfg.tilt && window.DeviceOrientationEvent) {
        window.addEventListener('deviceorientation', function (e) {
          if (e.gamma == null) return;
          tiltX = clamp(e.gamma / 45, -1, 1) * 0.6;
          start();
        }, { passive: true });
      }

      // The hero can change size for reasons a resize event never reports: a
      // web font landing changes the strip below it, the theme editor redraws,
      // a container query fires. Watch the element itself.
      var timer = 0;
      function onSizeChange() {
        clearTimeout(timer);
        timer = setTimeout(function () {
          var prevW = size.w, prevH = size.h;
          measure();
          if (Math.abs(size.w - prevW) < 2 && Math.abs(size.h - prevH) < 2) return;

          buildWalls();
          if (Math.abs(size.w - prevW) >= 2) {
            // Width drives sprite sizes, so everything is rebuilt in place.
            endDrag();
            removeCursorBody();
            buildItems(true);
          } else {
            // Height only: keep the composition, just lift anything the new
            // floor has left hanging below the hero.
            var ground = floorY();
            items.forEach(function (it) {
              var over = it.body.bounds.max.y - ground;
              if (over <= 0) return;
              Sleeping.set(it.body, false);
              Body.setPosition(it.body, {
                x: it.body.position.x,
                y: it.body.position.y - over
              });
              syncPrev(it);
            });
          }
          start();
        }, 180);
      }

      window.addEventListener('resize', onSizeChange);
      if ('ResizeObserver' in window) new ResizeObserver(onSizeChange).observe(root);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(onSizeChange);

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) { if (started) start(); } else { stop(); }
          });
        }, { threshold: 0.02 }).observe(root);
      }
    }

    /* -------------------------------------------------------------- init */

    if (DEBUG) {
      debugCanvas = document.createElement('canvas');
      debugCanvas.className = 'gh-debug';
      root.appendChild(debugCanvas);
      debugCtx = debugCanvas.getContext('2d');
    }
    if (!cfg.shadow) root.classList.add('gh-no-shadow');
    measure();

    Promise.all(cfg.items.map(function (spec) {
      return loadImage(spec.src).then(function (img) {
        spec._img = img;
        spec._hull = traceUnitHull(img, cfg);
      }).catch(function () { spec._img = null; });
    })).then(function () {
      cfg.items = cfg.items.filter(function (s) { return s._img; });
      if (!cfg.items.length) return;
      measure();
      buildWalls();
      buildItems(false);
      bind();
      started = true;
      root.classList.add('gh-ready');
      start();

      // Handy from the console / theme editor. getItems() is a function on
      // purpose: buildItems() replaces the array, so a stored reference goes
      // stale after a reset.
      root.gravityHero = {
        engine: engine,
        getItems: function () { return items; },
        reset: function () { measure(); buildWalls(); buildItems(false); start(); },
        state: function () {
          return {
            fps: fps, running: running, pending: pending, size: size,
            awake: items.filter(function (it) { return it.visible && !it.body.isSleeping; }).length,
            cursorBody: !!cursorBody
          };
        }
      };
    });
  }

  function init() {
    document.querySelectorAll('[data-gravity-hero]').forEach(function (root) {
      if (root.__ghInit) return;
      root.__ghInit = true;
      createHero(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify theme editor: re-run after a section or block is re-rendered.
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:block:select', init);
})();
